import { applyFilters, parseFilters, type FilterValues } from "@/lib/filters";

import type { ApiParams } from "./api";
import type { SourcingTab } from "./routes";

export const SHARED = ["item", "funnel", "phase", "option", "geo", "dropped"] as const;
export type SharedName = (typeof SHARED)[number];
export type Shared = Record<SharedName, string | null>;

export const LAST_ITEM_KEY = "sourcing_funnel.last_item";
export const CLEAR_SHARED: FilterValues = { funnel: null, phase: null, option: null, geo: null, dropped: null };

export const PAGE_FILTERS = [
  "feature", "criterion", "criteria_phase", "cost_type", "incoterm", "variant", "destination", "origin", "year", "stall",
  "sample", "evaluator",
] as const;
export type PageFilterName = (typeof PAGE_FILTERS)[number];

/** What "Clear filters" resets: every shared and page filter except the item and the variant a page needs. */
export const CLEAR_ALL: FilterValues = {
  ...CLEAR_SHARED,
  feature: null, criterion: null, criteria_phase: null, cost_type: null, incoterm: null,
  destination: null, origin: null, year: null, stall: null, sample: null, evaluator: null,
};

export function readShared(search: Record<string, unknown>): Shared {
  const values = parseFilters(search);
  const out = {} as Shared;
  for (const name of SHARED) {
    const value = values[name];
    out[name] = typeof value === "string" && value !== "" ? value : null;
  }
  return out;
}

export function apiParams(tab: SourcingTab, shared: Shared, extra: ApiParams = {}): ApiParams {
  const out: ApiParams = {};
  for (const name of SHARED) {
    const value = shared[name];
    if (value === null || !tab.accepts.includes(name)) continue;
    out[name] = value;
  }
  return { ...out, ...extra };
}

export function onFilterChange(search: Record<string, unknown>, next: FilterValues): Record<string, unknown> {
  const current = readShared(search);
  // The bar's Clear sends every shared filter as null: it resets them all, the item included.
  const allNull = SHARED.every((name) => (next[name] ?? null) === null);
  const itemChanged = "item" in next && (next.item ?? null) !== current.item;
  const optionChanged = "option" in next && (next.option ?? null) !== current.option;
  const out = applyFilters(search, itemChanged ? { ...next, option: null } : next);
  if (itemChanged || optionChanged || allNull) delete out.option;
  // A variant belongs to the item it was picked for; PageFilters drops any other page filter left stale.
  if (current.item !== null && readShared(out).item !== current.item) delete out.f_variant;
  // So does Item Overview's cost line, also when an item is set where none was (the remembered item).
  if (readShared(out).item !== current.item) delete out.f_line;
  delete out.page;
  delete out.lpage;
  return out;
}

/**
 * A bar change on Option Overview, whose Option pill shows the option on screen (`viewing`: the
 * link's `option`, else the filter). Picking another entry there replaces the link's option, and
 * the bar's Clear removes it with the item; a change to any other pill leaves it alone.
 */
export function onOverviewFilterChange(
  search: Record<string, unknown>,
  next: FilterValues,
  viewing: string | null,
): Record<string, unknown> {
  const current = readShared(search);
  const picked = "option" in next && (next.option ?? null) !== viewing;
  if (!picked) return onFilterChange(search, { ...next, option: current.option });
  const out = onFilterChange(search, next);
  delete out.option;
  return out;
}

export function readLastItem(): string | null {
  try {
    return window.localStorage.getItem(LAST_ITEM_KEY);
  } catch {
    return null;
  }
}

export function writeLastItem(item: string | null): void {
  try {
    if (item) window.localStorage.setItem(LAST_ITEM_KEY, item);
    else window.localStorage.removeItem(LAST_ITEM_KEY);
  } catch {
    // Private window or blocked storage: the default is a convenience, never required.
  }
}
